'use client';

import { useEffect, useState } from 'react';

import { useApiClient } from '@/hooks/useApiClient';

/**
 * Resolves the shareable feedback link.
 * 1. Tries the postMessage channel (works inside the Wix editor iframe).
 * 2. Falls back to /api/sharable-link (works in dev and standalone partner view).
 */
export function useResolvedShareUrl(
  getSharableLink: () => Promise<string | null>,
  metaSiteId?: string,
  enabled = true,
): string | null {
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const apiClient = useApiClient();

  useEffect(() => {
    if (!enabled || shareUrl) return;

    async function resolve() {
      const fromEditor = await getSharableLink();
      if (fromEditor) {
        setShareUrl(fromEditor);
        return;
      }
      if (metaSiteId) {
        try {
          // Use the authenticated client so the BFF's permission check passes;
          // a raw fetch omits the Wix instance Authorization header.
          const res = await apiClient.get<{ shareUrl: string | null }>(
            `/api/sharable-link?metaSiteId=${encodeURIComponent(metaSiteId)}`,
          );
          if (res.data.shareUrl) {
            setShareUrl(res.data.shareUrl);
          }
        } catch {
          // ignore fetch errors
        }
      }
    }

    void resolve();
  }, [apiClient, enabled, getSharableLink, metaSiteId, shareUrl]);

  return shareUrl;
}
